/**
 * Sort in-person tutor subject options by the shared catalog order (POC-716).
 * Unknown subjects stay after the seven priority entries. Option values are not changed.
 */

import {
  INPERSON_SUBJECT_ALIASES,
  INPERSON_SUBJECT_ORDER,
  type InpersonSubjectSlug,
} from '@/constants/shared-catalog';

/** Normalise a subject label to an ASCII slug for alias and rank lookup. */
function inpersonSubjectSortKey(label: string): string {
  let folded = label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replaceAll('.', '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  if (folded.startsWith('-')) folded = folded.slice(1);
  if (folded.endsWith('-')) folded = folded.slice(0, -1);
  return INPERSON_SUBJECT_ALIASES[folded] ?? folded;
}

/** Returns the priority rank of a subject label (lower = earlier). Unknown subjects rank last. */
function inpersonSubjectRank(label: string): number {
  const index = INPERSON_SUBJECT_ORDER.indexOf(
    inpersonSubjectSortKey(label) as InpersonSubjectSlug,
  );
  return index === -1 ? INPERSON_SUBJECT_ORDER.length : index;
}

/** Sort subject option items by the in-person display order, then alphabetically. */
export function sortByInpersonSubjectLabel<T extends { label: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    const rank = inpersonSubjectRank(a.label) - inpersonSubjectRank(b.label);
    if (rank !== 0) return rank;
    return a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
  });
}
